import { OrthoImage } from "@nova/protobuf"
import { generateGenericCases } from "../../generateCases"
import { Builder } from "../../sockets"
import {
    CreateOrthoImageArgs,
    getDefaultOrthoImageCreateArgs,
    UpdateOrthoImageArgs,
} from "@nova/serializer/src/client/validators/images/ortho"
import {
    addEntitySocket,
    batchAddEntitySocket,
    deleteEntitySocket,
    updateEntitySocket,
} from "features/entity-caching/project/slice"
import { createAction as createReduxAction } from "@reduxjs/toolkit"

const batchCreateOrthoImagesAction = createReduxAction<{
    entities: Pick<OrthoImage, CreateOrthoImageArgs[number]>[]
    context: { flightId: string }
}>("batchCreateOrthoImages")

const {
    create: createOrthoImage,
    update: updateOrthoImage,
    delete: deleteOrthoImage,
    createAction,
    updateAction,
    deleteAction,
} = generateGenericCases<
    OrthoImage, // entity type
    CreateOrthoImageArgs, // create args to send to server
    [
        "longitude",
        "latitude",
        "altitude",
        "height",
        "pitch",
        "yaw",
        "roll",
        "takenAt",
        "fov",
        "id",
        "make",
        "focalLength",
        "size",
        "imageHeight",
        "imageWidth",
        "imageType",
    ], // create args required by action
    UpdateOrthoImageArgs, // update args
    { flightId: string } // context
>("OrthoImage")

export const addOrthoImagesToBuilder = (builder: Builder) =>
    builder
        .addCase(createAction, (store, { payload: { data, context } }, socket) => {
            const optimisticData = OrthoImage.create({
                ...data,
                ...getDefaultOrthoImageCreateArgs(),
            }) as OrthoImage

            createOrthoImage(socket, { ...data, id: optimisticData.id }, context)
            store.dispatch(
                addEntitySocket({
                    data: optimisticData,
                    path: ["orthoFlights", { id: context.flightId }, "images"],
                })
            )
        })
        .addCase(updateAction, (store, { payload: { id, updates, context } }, socket) => {
            updateOrthoImage(socket, id, updates, context)
            store.dispatch(
                updateEntitySocket({
                    updates,
                    path: ["orthoFlights", { id: context.flightId }, "images", { id }],
                })
            )
        })
        .addCase(deleteAction, (store, { payload: { id, context } }, socket) => {
            deleteOrthoImage(socket, id, context)
            store.dispatch(
                deleteEntitySocket({
                    path: ["orthoFlights", { id: context.flightId }, "images", { id }],
                })
            )
        })
        .addCase(
            batchCreateOrthoImagesAction,
            (store, { payload: { entities, context } }, socket) => {
                const optimisticEntities = entities.map(
                    (e) =>
                        OrthoImage.create({
                            ...e,
                            ...getDefaultOrthoImageCreateArgs(),
                        }) as OrthoImage
                )
                socket.emit("batchCreateOrthoImage", optimisticEntities, context)
                store.dispatch(
                    batchAddEntitySocket({
                        entities: optimisticEntities,
                        path: ["orthoFlights", { id: context.flightId }, "images"],
                    })
                )
            }
        )

export {
    createAction as createOrthoImage,
    updateAction as updateOrthoImage,
    deleteAction as deleteOrthoImage,
    batchCreateOrthoImagesAction as batchCreateOrthoImages,
}
